// Multi-currency: display + real charging via Stripe/NOWPayments (bank transfer stays AUD).
// Rates come from the backend (cached from a live FX feed); choice persists in localStorage
// and auto-detects from the visitor's country on first visit.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const CURRENCIES = [
  ["aud", "AUD", "Australian dollar"],
  ["usd", "USD", "US dollar"],
  ["eur", "EUR", "Euro"],
  ["gbp", "GBP", "British pound"],
  ["nzd", "NZD", "New Zealand dollar"],
  ["cad", "CAD", "Canadian dollar"],
  ["sgd", "SGD", "Singapore dollar"],
  ["hkd", "HKD", "Hong Kong dollar"],
  ["chf", "CHF", "Swiss franc"],
  ["sek", "SEK", "Swedish krona"],
  ["nok", "NOK", "Norwegian krone"],
  ["dkk", "DKK", "Danish krone"],
  ["pln", "PLN", "Polish złoty"],
  ["czk", "CZK", "Czech koruna"],
  ["huf", "HUF", "Hungarian forint"],
  ["ron", "RON", "Romanian leu"],
  ["bgn", "BGN", "Bulgarian lev"],
  ["jpy", "JPY", "Japanese yen"],
  ["krw", "KRW", "South Korean won"],
  ["inr", "INR", "Indian rupee"],
  ["brl", "BRL", "Brazilian real"],
  ["mxn", "MXN", "Mexican peso"],
  ["aed", "AED", "UAE dirham"],
  ["zar", "ZAR", "South African rand"],
  ["thb", "THB", "Thai baht"],
  ["myr", "MYR", "Malaysian ringgit"],
  ["php", "PHP", "Philippine peso"],
  ["idr", "IDR", "Indonesian rupiah"],
  ["try", "TRY", "Turkish lira"],
  ["ils", "ILS", "Israeli shekel"],
];
export const CURRENCY_CODES = CURRENCIES.map(([c]) => c);
const ZERO_DECIMAL = new Set(["jpy", "krw"]);

let ratesCache = null;
let ratesPromise = null;

export function getRates() {
  if (ratesCache) return Promise.resolve(ratesCache);
  if (!ratesPromise) {
    ratesPromise = api
      .get("/rates")
      .then(({ data }) => {
        ratesCache = data.rates || { aud: 1 };
        return ratesCache;
      })
      .catch(() => {
        ratesCache = { aud: 1 };
        return ratesCache;
      });
  }
  return ratesPromise;
}

export function getCurrency() {
  const c = (localStorage.getItem("desync_currency") || "").toLowerCase();
  return CURRENCY_CODES.includes(c) ? c : "aud";
}

export function setCurrency(code) {
  localStorage.setItem("desync_currency", code.toLowerCase());
  window.dispatchEvent(new Event("desync-currency"));
}

// convert an AUD amount to the current currency and format it
export function fmt(audAmount, rates, currency) {
  const cur = currency || getCurrency();
  const rate = (rates && rates[cur]) || 1;
  const value = (Number(audAmount) || 0) * rate;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: cur.toUpperCase(),
    minimumFractionDigits: ZERO_DECIMAL.has(cur) ? 0 : 2,
    maximumFractionDigits: ZERO_DECIMAL.has(cur) ? 0 : 2,
  }).format(value);
}

// tiny live-price helper for sub-cent unit prices (e.g. "0.13c each")
export function fmtUnitCents(audUnit, rates, currency) {
  const cur = currency || getCurrency();
  const rate = (rates && rates[cur]) || 1;
  const cents = (Number(audUnit) || 0) * rate * 100;
  return `${cents.toFixed(cents < 1 ? 2 : 1)}c`;
}

// hook: re-renders when currency changes, provides rates + formatter
export function useCurrency() {
  const [currency, setCur] = useState(getCurrency());
  const [rates, setRates] = useState(ratesCache);
  useEffect(() => {
    getRates().then(setRates);
    const onChange = () => setCur(getCurrency());
    window.addEventListener("desync-currency", onChange);
    return () => window.removeEventListener("desync-currency", onChange);
  }, []);
  return { currency, rates, fmt: (audAmount) => fmt(audAmount, rates, currency) };
}

// first-visit geo detection (best-effort, silent failures)
export function autoDetectCurrency() {
  if (localStorage.getItem("desync_currency")) return;
  fetch("https://ipapi.co/json/")
    .then((r) => r.json())
    .then((d) => {
      const c = (d.currency || "").toLowerCase();
      if (CURRENCY_CODES.includes(c)) setCurrency(c);
    })
    .catch(() => {});
}

// format an order's actual charged amount (orders store AUD total + charged currency)
export function fmtOrder(order, fmtFn) {
  if (order.charge_total != null && order.currency) {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: order.currency.toUpperCase(),
      minimumFractionDigits: ZERO_DECIMAL.has(order.currency) ? 0 : 2,
      maximumFractionDigits: ZERO_DECIMAL.has(order.currency) ? 0 : 2,
    }).format(order.charge_total);
  }
  return fmtFn ? fmtFn(order.total) : `A$${Number(order.total || 0).toFixed(2)}`;
}
